/**
 * PromotionsTab Component
 *
 * Manages time-bound promotional discounts that can target specific
 * pricing tiers, customer types, products, and grades.
 * Auto-applied during sale creation using best-price-wins logic.
 */

import { useState, useEffect, useCallback } from 'react'
import { toLocalDateString } from '../utils/dateUtils'
import {
  fetchPromotions,
  fetchPriceTiers,
  fetchProducts,
  fetchProductTypes,
  createPromotion,
  updatePromotion,
  deletePromotion
} from '../supabase-client'
import { supabase } from '@/supabase-client'
import { useDataCache } from '../contexts/DataCacheContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { OdinButton } from '@/components/ui/odin-button'
import { EditIcon } from '@/components/ui/icons/EditIcon'
import { Tag, AlertCircle, Plus } from 'lucide-react'

const emptyForm = {
  name: '',
  description: '',
  price_tier_id: '',
  customer_type_id: '',
  product_id: '',
  product_type_id: '',
  discount_percent: '',
  start_date: '',
  end_date: '',
  active: true
}

function getPromotionStatus(promo) {
  const today = toLocalDateString(new Date())
  if (!promo.active) return 'inactive'
  if (promo.start_date > today) return 'upcoming'
  if (promo.end_date < today) return 'expired'
  return 'active'
}

const statusStyles = {
  active: 'bg-green-100 text-green-800',
  upcoming: 'bg-amber-100 text-amber-800',
  expired: 'bg-red-100 text-red-800',
  inactive: 'bg-slate-100 text-slate-600'
}

const statusLabels = {
  active: 'Active',
  upcoming: 'Upcoming',
  expired: 'Expired',
  inactive: 'Inactive'
}

export default function PromotionsTab() {
  const { invalidateCache } = useDataCache()
  const [promotions, setPromotions] = useState([])
  const [priceTiers, setPriceTiers] = useState([])
  const [products, setProducts] = useState([])
  const [productTypes, setProductTypes] = useState([])
  const [customerTypes, setCustomerTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [promos, tiers, prods, types, ctRes] = await Promise.all([
        fetchPromotions(),
        fetchPriceTiers(),
        fetchProducts(),
        fetchProductTypes(),
        supabase.from('customer_types').select('id, name').order('name')
      ])
      setPromotions(promos)
      setPriceTiers(tiers)
      setProducts(prods.filter(p => p.active))
      setProductTypes(types.filter(t => t.active))
      setCustomerTypes(ctRes.data || [])
    } catch (err) {
      console.error('Failed to load promotions data:', err)
      setError('Failed to load promotions data.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleAddNew = () => {
    setEditingItem(null)
    setForm(emptyForm)
    setShowForm(true)
  }

  const handleEdit = (promo) => {
    setEditingItem(promo)
    setForm({
      name: promo.name || '',
      description: promo.description || '',
      price_tier_id: promo.price_tier_id || '',
      customer_type_id: promo.customer_type_id || '',
      product_id: promo.product_id || '',
      product_type_id: promo.product_type_id || '',
      discount_percent: promo.discount_percent?.toString() || '',
      start_date: promo.start_date || '',
      end_date: promo.end_date || '',
      active: promo.active ?? true
    })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this promotion?')) return
    try {
      await deletePromotion(id)
      invalidateCache('activePromotions')
      await loadData()
    } catch (err) {
      console.error('Failed to delete promotion:', err)
      setError('Failed to delete promotion.')
    }
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Promotion name is required.')
      return
    }
    if (!form.discount_percent || parseFloat(form.discount_percent) <= 0 || parseFloat(form.discount_percent) > 100) {
      setError('Discount must be between 0 and 100.')
      return
    }
    if (!form.start_date || !form.end_date) {
      setError('Start and end dates are required.')
      return
    }
    if (form.end_date < form.start_date) {
      setError('End date must be on or after start date.')
      return
    }

    try {
      setSaving(true)
      setError(null)

      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        price_tier_id: form.price_tier_id || null,
        customer_type_id: form.customer_type_id || null,
        product_id: form.product_id || null,
        product_type_id: form.product_type_id || null,
        discount_percent: parseFloat(form.discount_percent),
        start_date: form.start_date,
        end_date: form.end_date,
        active: form.active
      }

      if (editingItem) {
        await updatePromotion(editingItem.id, payload)
      } else {
        await createPromotion(payload)
      }

      invalidateCache('activePromotions')
      setShowForm(false)
      setEditingItem(null)
      await loadData()
    } catch (err) {
      console.error('Failed to save promotion:', err)
      setError('Failed to save promotion: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-[#009689]" />
                Promotions
              </CardTitle>
              <CardDescription>
                Time-bound discounts targeting specific tiers, customer types, products, and grades.
                Best-price-wins when competing with volume discounts.
              </CardDescription>
            </div>
            <Button variant="create" onClick={handleAddNew}>
              <Plus className="h-4 w-4 mr-1" />
              Add Promotion
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Customer Type</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead>End</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promotions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                      No promotions yet. Click "Add Promotion" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  promotions.map(promo => {
                    const status = getPromotionStatus(promo)
                    return (
                      <TableRow key={promo.id}>
                        <TableCell className="font-medium">
                          {promo.name}
                          {promo.description && (
                            <div className="text-xs text-muted-foreground">{promo.description}</div>
                          )}
                        </TableCell>
                        <TableCell>{promo.price_tier?.display_name || 'All'}</TableCell>
                        <TableCell>{promo.customer_type?.name || 'All'}</TableCell>
                        <TableCell>{promo.product?.strain || 'All'}</TableCell>
                        <TableCell>{promo.product_type?.name || 'All'}</TableCell>
                        <TableCell>{promo.discount_percent}%</TableCell>
                        <TableCell className="text-sm">{promo.start_date}</TableCell>
                        <TableCell className="text-sm">{promo.end_date}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status]}`}>
                            {statusLabels[status]}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <OdinButton variant="table-action" size="small" leftIcon={<EditIcon />} onClick={() => handleEdit(promo)}>Edit</OdinButton>
                            <Button variant="destructive" size="sm" onClick={() => handleDelete(promo.id)}>Delete</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={(open) => {
        setShowForm(open)
        if (!open) {
          setEditingItem(null)
          setError(null)
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Promotion' : 'Add Promotion'}</DialogTitle>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="e.g. Spring Oyster Sale"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Optional description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Pricing Tier</Label>
                <Select value={form.price_tier_id} onValueChange={(v) => updateField('price_tier_id', v === 'all' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Tiers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    {priceTiers.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Customer Type</Label>
                <Select value={form.customer_type_id} onValueChange={(v) => updateField('customer_type_id', v === 'all' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {customerTypes.map(ct => (
                      <SelectItem key={ct.id} value={ct.id}>{ct.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Product</Label>
                <Select value={form.product_id} onValueChange={(v) => updateField('product_id', v === 'all' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Products" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.strain}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Grade</Label>
                <Select value={form.product_type_id} onValueChange={(v) => updateField('product_type_id', v === 'all' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Grades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Grades</SelectItem>
                    {productTypes.map(pt => (
                      <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Discount Percentage *</Label>
              <Input
                type="number"
                min="0.01"
                max="100"
                step="0.5"
                value={form.discount_percent}
                onChange={(e) => updateField('discount_percent', e.target.value)}
                placeholder="e.g. 10"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => updateField('start_date', e.target.value)}
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => updateField('end_date', e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={form.active}
                onCheckedChange={(checked) => updateField('active', checked)}
              />
              <Label>Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
